import { Input, Select } from "@/components/base/FormControls";
import { cn } from "@/utils/cn";
import type { ReportFilters, ReportOption, StatutFilterConfig } from "./types";

interface ReportFiltersPanelProps {
  filters: ReportFilters;
  onChange: (patch: Partial<ReportFilters>) => void;
  onReset: () => void;
  showProprietaire: boolean;
  immeubleOptions: ReportOption[];
  bienOptions: ReportOption[];
  locataireOptions: ReportOption[];
  proprietaireOptions: ReportOption[];
  statutConfig: StatutFilterConfig;
  isFiltering: boolean;
}

function isoDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

type PresetKey = "mois" | "trimestre" | "annee" | "tout";

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "mois", label: "Ce mois" },
  { key: "trimestre", label: "3 mois" },
  { key: "annee", label: "Cette année" },
  { key: "tout", label: "Tout" },
];

export default function ReportFiltersPanel({
  filters,
  onChange,
  onReset,
  showProprietaire,
  immeubleOptions,
  bienOptions,
  locataireOptions,
  proprietaireOptions,
  statutConfig,
  isFiltering,
}: ReportFiltersPanelProps) {
  const applyPreset = (preset: PresetKey) => {
    const now = new Date();
    if (preset === "tout") {
      onChange({ dateFrom: "", dateTo: "" });
      return;
    }
    if (preset === "mois") {
      onChange({ dateFrom: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)), dateTo: isoDate(now) });
      return;
    }
    if (preset === "trimestre") {
      onChange({ dateFrom: isoDate(new Date(now.getFullYear(), now.getMonth() - 2, 1)), dateTo: isoDate(now) });
      return;
    }
    onChange({ dateFrom: isoDate(new Date(now.getFullYear(), 0, 1)), dateTo: isoDate(now) });
  };

  const activePreset: PresetKey | null = (() => {
    const now = new Date();
    if (!filters.dateFrom && !filters.dateTo) return "tout";
    if (filters.dateFrom === isoDate(new Date(now.getFullYear(), now.getMonth(), 1)) && filters.dateTo === isoDate(now)) {
      return "mois";
    }
    if (
      filters.dateFrom === isoDate(new Date(now.getFullYear(), now.getMonth() - 2, 1)) &&
      filters.dateTo === isoDate(now)
    ) {
      return "trimestre";
    }
    if (filters.dateFrom === isoDate(new Date(now.getFullYear(), 0, 1)) && filters.dateTo === isoDate(now)) {
      return "annee";
    }
    return null;
  })();

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">2. Filtrez les données</h3>
        {isFiltering && (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
          >
            <i className="ri-refresh-line text-sm" aria-hidden="true" />
            Réinitialiser les filtres
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-xs font-label font-medium text-foreground-700">Période</span>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => applyPreset(preset.key)}
              className={cn(
                "whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-label font-medium transition-colors cursor-pointer",
                activePreset === preset.key
                  ? "border-primary-400 bg-primary-50 text-primary-800"
                  : "border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100"
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="report-date-from" className="text-xs font-label font-medium text-foreground-700">
            Du
          </label>
          <Input
            id="report-date-from"
            type="date"
            value={filters.dateFrom}
            onChange={(event) => onChange({ dateFrom: event.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="report-date-to" className="text-xs font-label font-medium text-foreground-700">
            Au
          </label>
          <Input
            id="report-date-to"
            type="date"
            value={filters.dateTo}
            onChange={(event) => onChange({ dateTo: event.target.value })}
          />
        </div>

        {showProprietaire && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="report-proprietaire" className="text-xs font-label font-medium text-foreground-700">
              Propriétaire
            </label>
            <Select
              id="report-proprietaire"
              value={filters.proprietaireId}
              onChange={(event) =>
                onChange({ proprietaireId: event.target.value, immeubleId: "", bienId: "" })
              }
              options={proprietaireOptions}
            />
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="report-immeuble" className="text-xs font-label font-medium text-foreground-700">
            Immeuble
          </label>
          <Select
            id="report-immeuble"
            value={filters.immeubleId}
            onChange={(event) => onChange({ immeubleId: event.target.value, bienId: "" })}
            options={immeubleOptions}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="report-bien" className="text-xs font-label font-medium text-foreground-700">
            Bien
          </label>
          <Select
            id="report-bien"
            value={filters.bienId}
            onChange={(event) => onChange({ bienId: event.target.value })}
            options={bienOptions}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="report-locataire" className="text-xs font-label font-medium text-foreground-700">
            Locataire
          </label>
          <Select
            id="report-locataire"
            value={filters.locataireId}
            onChange={(event) => onChange({ locataireId: event.target.value })}
            options={locataireOptions}
          />
        </div>

        {statutConfig.options.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="report-statut" className="text-xs font-label font-medium text-foreground-700">
              {statutConfig.label}
            </label>
            <Select
              id="report-statut"
              value={filters.statut}
              onChange={(event) => onChange({ statut: event.target.value })}
              options={statutConfig.options}
            />
          </div>
        )}
      </div>
    </section>
  );
}